// @flow

import { useState, useCallback } from 'react';
import { TextInput } from './Input';

import type { Node } from 'react';

// EMAIL_REGEX mirrors the Ruby EmailValidator::EMAIL_ADDRESS_FORMAT used by the
// API so that client-side and server-side validation stay in sync.
// - Unquoted local: Unicode letters/digits + RFC-allowed specials, no spaces.
// - Quoted local: double-quoted string with optional backslash-escape sequences.
// - Domain: dot-separated labels; each label starts and ends with a letter or
//   digit, may contain hyphens, max 63 chars per label, at least one dot
//   required (mirrors the registerable_domain? check).
// The /u flag enables Unicode property escapes (\p{L}, \p{N}, \p{C}).
//
// IMPORTANT: This regex intentionally allows UTF-8mb4 characters (emoji,
// supplementary CJK, etc.) because they are valid per RFC 6530/6531.
// The API currently rejects such addresses with a 422 error due to a MySQL
// utf8mb3 column limitation. Until the database is migrated to utf8mb4, users
// who enter addresses containing those characters will pass client validation
// but receive an API error. Once the database is fixed the API restriction
// will be lifted and no client change will be needed.
//
// NOTE: this constant intentionally lives here rather than in SignUp.lib.js —
// moving it there previously caused the regex to behave incorrectly in jest.
export const EMAIL_REGEX = /^(?:[\p{L}\p{N}.!#$%&'*+/=?^_`{|}~-]+|"(?:[^\p{C}"\\]|\\.)*")@[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?(?:\.[\p{L}\p{N}](?:[\p{L}\p{N}-]{0,61}[\p{L}\p{N}])?)+$/u;

/**
 * Returns the validation error string if the value is not a valid email
 * address, or null if it is valid. Allows callers to validate without
 * importing EMAIL_REGEX directly.
 */
export const validateEmail = (value: ?string): ?string =>
    (!value || !EMAIL_REGEX.test(value))
        ? 'Please enter a valid email address'
        : null;

type EmailValidationHook = {
    error: ?string,
    validate: (value: string) => boolean,
    clear: () => void,
};

/**
 * Hook for validate-on-submit email validation.
 * - validate(value) sets the error and returns false if invalid, clears the
 *   error and returns true if valid.
 * - clear() resets the error, e.g. when the user edits the field.
 * - error holds the current validation error string.
 */
export function useEmailValidation(): EmailValidationHook {
    const [error, setError] = useState<?string>(null);

    const validate = useCallback((value: string): boolean => {
        const err = validateEmail(value);
        setError(err);
        return err == null;
    }, []);

    const clear = useCallback(() => setError(null), []);

    return { error, validate, clear };
}

type EmailInputProps = {
    value: string,
    onChange: (value: string) => void,
    label?: ?string,
    errorText?: ?string,
    optional?: boolean,
    disabled?: boolean,
    autoFocus?: boolean,
    focusSelectAll?: boolean,
    readOnly?: ?boolean,
    hint?: Node,
    className?: ?string,
};

/**
 * A pre-configured TextInput for email addresses.
 *
 * Sets inputMode='email' for mobile keyboard optimisation and
 * autoComplete='email' for browser autofill. All other TextInput
 * props are forwarded.
 *
 * For validate-on-submit behaviour, use the useEmailValidation() hook
 * exported from this module; it manages error state and keeps EMAIL_REGEX
 * private to this file.
 */
export const EmailInput = ({ label = 'Email Address', ...props }: EmailInputProps): React$Node => (
    <TextInput
        inputMode='email'
        autoComplete='email'
        label={label}
        {...props}
    />
);
